import { z } from 'zod';
import {
  uuidSchema,
  currencyCodeSchema,
  decimalStringSchema,
  positiveDecimalSchema,
  isoDateSchema,
  ibanSchema,
  bicSchema,
} from './common';

export const moneyAccountTypeSchema = z.enum([
  'cash',
  'bank_account',
  'debit_card',
  'credit_card',
  'prepaid_card',
  'e_wallet',
  'savings',
  'investment',
  'crypto',
  // 🎁 Yan haklar / benefit kartlar — period totals'a (gelir/gider/net) dahil edilmez.
  // Sadece kendi hesap bakiyesinde takip edilir. Net Worth'e de eklenmez.
  'meal_card',
  'fuel_card',
  'gift_card',
  'custom',
]);
export type MoneyAccountType = z.infer<typeof moneyAccountTypeSchema>;

/**
 * Bu tipteki para hesaplarına bağlı transactionlar:
 *   - Period totals (income/expense/balance) hesabına dahil edilmez
 *   - Net Worth toplamlarına dahil edilmez
 *   - Sadece hesap bakiyesinde (yükleme/harcama akışı) görünür
 * Hem backend (snapshot/reports/cashflow) hem frontend birlikte kullanır.
 */
export const BENEFIT_ACCOUNT_TYPES = ['meal_card', 'fuel_card', 'gift_card'] as const;
export type BenefitAccountType = (typeof BENEFIT_ACCOUNT_TYPES)[number];

export function isBenefitAccountType(type: string | null | undefined): boolean {
  return type === 'meal_card' || type === 'fuel_card' || type === 'gift_card';
}

export const accountFlowSchema = z.enum(['debit', 'credit']);
export type AccountFlow = z.infer<typeof accountFlowSchema>;

export const transferTypeSchema = z.enum([
  'internal', // Hesaplar arası (kullanıcının kendi hesapları)
  'sepa_credit_transfer', // SCT — standart SEPA havalesi
  'sepa_instant', // SCT Inst — 10 saniye içinde
  'sepa_direct_debit', // SDD — alacaklı tarafından çekilir
  'swift', // EU dışı uluslararası
]);
export type TransferType = z.infer<typeof transferTypeSchema>;

export const createMoneyAccountSchema = z.object({
  type: moneyAccountTypeSchema,
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  icon: z.string().max(50).optional(),
  currency: currencyCodeSchema.default('TRY'),
  openingBalance: decimalStringSchema.optional().default('0'),
  includeInNetWorth: z.boolean().optional().default(true),
  isActive: z.boolean().optional().default(true),
  sortOrder: z.number().int().optional(),
  /**
   * Hesabın bağlı olduğu bütçe (banka importunun hedefi). Hesap SAHİBİNİN silinmemiş bütçesi
   * olmalı — sunucu doğrular; yok ve yabancı bütçe için aynı 400 MONEY_ACCOUNT_BUDGET_NOT_FOUND.
   * null → bağı temizle (update), undefined → no-op.
   */
  budgetId: uuidSchema.nullable().optional(),
  // 🏠 Aile (household) ortak hesabı işareti — NULL=kişisel, uuid=grup ortak.
  // null gönderildiğinde clear; undefined → no-op (sadece update için anlamlı).
  householdGroupId: uuidSchema.nullable().optional(),
  creditLimit: positiveDecimalSchema.optional(),
  statementDay: z.number().int().min(1).max(31).optional(),
  dueDay: z.number().int().min(1).max(31).optional(),
  minPaymentRate: z.number().min(0).max(100).optional(),
  iban: ibanSchema.optional(),
  bic: bicSchema.optional(),
  /**
   * 💸 Bağlı funding hesabı: bu hesaptan gider yapıldığında bağlı hesaptan otomatik "Otomatik
   * Banka Takası" kurulur (ör. PayPal → Belfius). null = bağı temizle (update), undefined = no-op.
   */
  linkedMoneyAccountId: uuidSchema.nullable().optional(),
});
export type CreateMoneyAccountInput = z.infer<typeof createMoneyAccountSchema>;

export const updateMoneyAccountSchema = createMoneyAccountSchema.partial();
export type UpdateMoneyAccountInput = z.infer<typeof updateMoneyAccountSchema>;

export const reorderMoneyAccountsSchema = z.object({
  accountIds: z.array(uuidSchema).min(1),
});
export type ReorderMoneyAccountsInput = z.infer<typeof reorderMoneyAccountsSchema>;

const accountTransferBaseSchema = z.object({
  fromAccountId: uuidSchema,
  // Dahili transferde zorunlu; dış (SEPA/SWIFT) transferde boş olabilir (alıcı IBAN ile).
  // Boş string ('') → undefined (form Select boş seçeneği).
  toAccountId: z.preprocess((v) => (v === '' || v === null ? undefined : v), uuidSchema.optional()),
  fromAmount: positiveDecimalSchema,
  fromCurrency: currencyCodeSchema,
  toAmount: positiveDecimalSchema,
  toCurrency: currencyCodeSchema,
  exchangeRate: z
    .string()
    .optional()
    .transform((v) => (v && v.trim() !== '' ? v : undefined)),
  date: isoDateSchema,
  description: z.string().max(200).optional(),
  note: z.string().max(500).optional(),
  transferType: transferTypeSchema.optional().default('internal'),
  recipientIban: ibanSchema.optional(),
  recipientBic: bicSchema.optional(),
  recipientName: z.string().max(200).optional(),
  reference: z.string().max(140).optional(),
  recipientId: uuidSchema.optional(),
  executedAt: z.string().datetime().optional(),
});

const EXTERNAL_TRANSFER_TYPES = [
  'sepa_credit_transfer',
  'sepa_instant',
  'sepa_direct_debit',
  'swift',
];

export const createAccountTransferSchema = accountTransferBaseSchema
  .refine((d) => !d.toAccountId || d.fromAccountId !== d.toAccountId, {
    message: 'Kaynak ve hedef hesap aynı olamaz',
    path: ['toAccountId'],
  })
  .refine(
    (d) => EXTERNAL_TRANSFER_TYPES.includes(d.transferType ?? 'internal') || Boolean(d.toAccountId),
    { message: 'Dahili transfer için hedef hesap zorunlu', path: ['toAccountId'] },
  )
  .refine(
    (d) =>
      !EXTERNAL_TRANSFER_TYPES.includes(d.transferType ?? 'internal') || Boolean(d.recipientIban),
    { message: 'SEPA/SWIFT transferi için alıcı IBAN zorunlu', path: ['recipientIban'] },
  );
export type CreateAccountTransferInput = z.infer<typeof createAccountTransferSchema>;

export const updateAccountTransferSchema = accountTransferBaseSchema
  .omit({ fromAccountId: true, toAccountId: true })
  .partial();
export type UpdateAccountTransferInput = z.infer<typeof updateAccountTransferSchema>;

// ---- Transfer Recipient Book ----

export const createTransferRecipientSchema = z.object({
  name: z.string().min(1).max(200),
  iban: ibanSchema,
  bic: bicSchema.optional(),
  note: z.string().max(500).optional(),
});
export type CreateTransferRecipientInput = z.infer<typeof createTransferRecipientSchema>;

export const updateTransferRecipientSchema = createTransferRecipientSchema.partial();
export type UpdateTransferRecipientInput = z.infer<typeof updateTransferRecipientSchema>;

// ---- Grup hesap paylaşımı (grup üyeleri arası transfer) ----

export const setAccountShareSchema = z.object({
  groupId: uuidSchema,
  shared: z.boolean(),
});
export type SetAccountShareInput = z.infer<typeof setAccountShareSchema>;

export const closeStatementSchema = z.object({
  statementDate: isoDateSchema,
  dueDate: isoDateSchema,
  totalDebt: positiveDecimalSchema,
  minPayment: positiveDecimalSchema,
});
export type CloseStatementInput = z.infer<typeof closeStatementSchema>;

export const recordStatementPaymentSchema = z.object({
  paidAmount: positiveDecimalSchema,
  paymentDate: isoDateSchema.optional(),
  note: z.string().max(500).optional(),
});
export type RecordStatementPaymentInput = z.infer<typeof recordStatementPaymentSchema>;

/**
 * Banka hesabı bakiye eşitleme (reconciliation) girdisi. Kullanıcı gerçek banka
 * bakiyesini (targetBalance) girer; backend mevcut sistem bakiyesi ile farkı
 * `account_adjustments` tablosuna yazar. `targetBalance` negatif olabilir
 * (kredi kartı borç bakiyesi gibi).
 *
 * 📅 Eşitleme yalnız BUGÜN (sunucu UTC günü) için yapılır: `date` gönderilmeyebilir; gönderilirse
 * bugüne eşit olmalı, aksi halde 400 `RECONCILE_DATE_NOT_TODAY`. Taban, bugüne kadar
 * GERÇEKLEŞMİŞ bakiyedir (ileri tarihli transfer/düzeltmeler hariç). Geçmiş tarihli eşitleme
 * kapalı: kayıt tarihi valör tarihi olmadığından (ön-açılış kopyaları ay sonuna tarihli)
 * sonraki dönemin kalemleri düzeltmeye gömülüyordu (bkz. ledger-03/04).
 */
export const reconcileMoneyAccountSchema = z.object({
  targetBalance: decimalStringSchema,
  date: isoDateSchema.optional(),
  note: z.string().max(500).optional(),
});
export type ReconcileMoneyAccountInput = z.infer<typeof reconcileMoneyAccountSchema>;

export const reconcileMoneyAccountResultSchema = z.object({
  adjustmentId: uuidSchema.nullable(),
  previousBalance: decimalStringSchema,
  targetBalance: decimalStringSchema,
  delta: decimalStringSchema,
  noop: z.boolean(),
});
export type ReconcileMoneyAccountResult = z.infer<typeof reconcileMoneyAccountResultSchema>;
